import type {
  AdoptionSeriesResponse,
  Granularity,
  HarnessDistributionResponse,
  ModelDistributionResponse,
  MonthlySeriesResponse,
  Origin,
  OutcomeSeriesResponse,
  OverviewResponse,
  PricingResponse,
  SessionDetailResponse,
  SessionsListResponse,
  TaskBreakdownResponse,
  SessionFilters,
  SessionSortDir,
  SessionSortField,
  TokenSeriesResponse,
  TopSkillsResponse,
  TopSprinklesResponse,
  TopUsersResponse,
  UserDetailResponse,
  UserFilters,
  UserSortDir,
  UserSortField,
  UsersDistributionResponse,
  UsersListResponse,
  BulkQuotaResponse,
  QuotaInfo,
  UsageResponse,
  UsageWindow,
} from "./types";

const API_BASE =
  import.meta.env.VITE_API_URL ||
  "https://llm-monitoring-worker.paolo-moz.workers.dev";

export class ApiAuthError extends Error {
  status: number;
  constructor(message: string, status: number) {
    super(message);
    this.status = status;
    this.name = "ApiAuthError";
  }
}

interface FetchOptions {
  token?: string | null;
  method?: string;
  body?: string;
}

async function fetchJson<T>(path: string, opts: FetchOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;
  const init: RequestInit = { headers, method: opts.method ?? "GET" };
  if (opts.body) init.body = opts.body;
  const response = await fetch(`${API_BASE}${path}`, init);
  if (!response.ok) {
    let message: string;
    try {
      const body = (await response.json()) as { error?: { message?: string } };
      message = body.error?.message ?? `HTTP ${response.status}`;
    } catch {
      message = `HTTP ${response.status}`;
    }
    if (response.status === 401 || response.status === 403) {
      throw new ApiAuthError(message, response.status);
    }
    throw new Error(message);
  }
  return response.json() as Promise<T>;
}

export interface DashboardWindow {
  from: string;
  to: string;
  origin: Origin;
}

function buildQuery(
  window: DashboardWindow,
  extra?: Record<string, string>,
): string {
  const params = new URLSearchParams({
    from: window.from,
    to: window.to,
    origin: window.origin,
    ...extra,
  });
  return `?${params.toString()}`;
}

export function fetchPricing(): Promise<PricingResponse> {
  return fetchJson<PricingResponse>("/v1/dashboard/pricing");
}

export function fetchOverview(
  window: DashboardWindow,
  token: string | null,
): Promise<OverviewResponse> {
  return fetchJson<OverviewResponse>(
    `/v1/dashboard/overview${buildQuery(window)}`,
    { token },
  );
}

export function fetchAdoptionSeries(
  window: DashboardWindow,
  token: string | null,
  granularity: Granularity = "day",
): Promise<AdoptionSeriesResponse> {
  return fetchJson<AdoptionSeriesResponse>(
    `/v1/dashboard/adoption-series${buildQuery(window, { granularity })}`,
    { token },
  );
}

export function fetchTokenSeries(
  window: DashboardWindow,
  token: string | null,
  granularity: Granularity = "day",
): Promise<TokenSeriesResponse> {
  return fetchJson<TokenSeriesResponse>(
    `/v1/dashboard/token-series${buildQuery(window, { granularity })}`,
    { token },
  );
}

export function fetchMonthlySeries(
  token: string | null,
  months = 12,
): Promise<MonthlySeriesResponse> {
  const params = new URLSearchParams({ months: String(months) });
  return fetchJson<MonthlySeriesResponse>(
    `/v1/dashboard/monthly-series?${params.toString()}`,
    { token },
  );
}

export function fetchOutcomeSeries(
  window: DashboardWindow,
  token: string | null,
): Promise<OutcomeSeriesResponse> {
  return fetchJson<OutcomeSeriesResponse>(
    `/v1/dashboard/outcome-series${buildQuery(window)}`,
    { token },
  );
}

export function fetchTopUsers(
  window: DashboardWindow,
  limit: number,
  token: string | null,
): Promise<TopUsersResponse> {
  return fetchJson<TopUsersResponse>(
    `/v1/dashboard/top-users${buildQuery(window, { limit: String(limit) })}`,
    { token },
  );
}

export function fetchTaskBreakdown(
  window: DashboardWindow,
  token: string | null,
): Promise<TaskBreakdownResponse> {
  return fetchJson<TaskBreakdownResponse>(
    `/v1/dashboard/task-breakdown${buildQuery(window)}`,
    { token },
  );
}

export function fetchSessions(
  window: DashboardWindow,
  pagination: {
    limit: number;
    offset: number;
    sortBy?: SessionSortField;
    sortDir?: SessionSortDir;
    filters?: SessionFilters;
  },
  token: string | null,
): Promise<SessionsListResponse> {
  const extra: Record<string, string> = {
    limit: String(pagination.limit),
    offset: String(pagination.offset),
  };
  if (pagination.sortBy) extra.sortBy = pagination.sortBy;
  if (pagination.sortDir) extra.sortDir = pagination.sortDir;
  if (pagination.filters) {
    const f = pagination.filters;
    if (f.tasks.length) extra.tasks = f.tasks.join(",");
    if (f.outcomes.length) extra.outcomes = f.outcomes.join(",");
    if (f.feels.length) extra.feels = f.feels.join(",");
    if (f.startedAfter) extra.startedAfter = f.startedAfter;
    if (f.startedBefore) extra.startedBefore = f.startedBefore;
    if (f.durationMinMin !== null) extra.durationMin = String(f.durationMinMin);
    if (f.durationMinMax !== null) extra.durationMax = String(f.durationMinMax);
    if (f.eventCountMin !== null) extra.eventsMin = String(f.eventCountMin);
    if (f.eventCountMax !== null) extra.eventsMax = String(f.eventCountMax);
    if (f.userIdQuery && f.userIdQuery !== "")
      extra.userIdQuery = f.userIdQuery;
    if (f.harnesses.length) extra.harnesses = f.harnesses.join(",");
  }
  return fetchJson<SessionsListResponse>(
    `/v1/dashboard/sessions${buildQuery(window, extra)}`,
    { token },
  );
}

export function fetchSessionDetail(
  sessionId: string,
  token: string | null,
): Promise<SessionDetailResponse> {
  return fetchJson<SessionDetailResponse>(
    `/v1/dashboard/sessions/${encodeURIComponent(sessionId)}`,
    { token },
  );
}

export function fetchTopSkills(
  window: DashboardWindow,
  limit: number,
  token: string | null,
): Promise<TopSkillsResponse> {
  return fetchJson<TopSkillsResponse>(
    `/v1/dashboard/top-skills${buildQuery(window, { limit: String(limit) })}`,
    { token },
  );
}

export function fetchTopSprinkles(
  window: DashboardWindow,
  limit: number,
  token: string | null,
): Promise<TopSprinklesResponse> {
  return fetchJson<TopSprinklesResponse>(
    `/v1/dashboard/top-sprinkles${buildQuery(window, { limit: String(limit) })}`,
    { token },
  );
}

export function fetchModelDistribution(
  window: DashboardWindow,
  token: string | null,
): Promise<ModelDistributionResponse> {
  return fetchJson<ModelDistributionResponse>(
    `/v1/dashboard/model-distribution${buildQuery(window)}`,
    { token },
  );
}

export function fetchHarnessDistribution(
  window: DashboardWindow,
  token: string | null,
): Promise<HarnessDistributionResponse> {
  return fetchJson<HarnessDistributionResponse>(
    `/v1/dashboard/harness-distribution${buildQuery(window)}`,
    { token },
  );
}

export function fetchUsersDistribution(
  window: DashboardWindow,
  token: string | null,
  granularity: Granularity = "day",
): Promise<UsersDistributionResponse> {
  return fetchJson<UsersDistributionResponse>(
    `/v1/dashboard/users-distribution${buildQuery(window, { granularity })}`,
    { token },
  );
}

export function fetchUsers(
  window: DashboardWindow,
  pagination: {
    limit: number;
    offset: number;
    sortBy?: UserSortField;
    sortDir?: UserSortDir;
    filters?: UserFilters;
  },
  token: string | null,
): Promise<UsersListResponse> {
  const extra: Record<string, string> = {
    limit: String(pagination.limit),
    offset: String(pagination.offset),
  };
  if (pagination.sortBy) extra.sortBy = pagination.sortBy;
  if (pagination.sortDir) extra.sortDir = pagination.sortDir;
  if (pagination.filters) {
    const f = pagination.filters;
    if (f.minSessions !== null) extra.minSessions = String(f.minSessions);
    if (f.minCompletionRate !== null)
      extra.minCompletionRate = String(f.minCompletionRate);
    if (f.tasks.length) extra.tasks = f.tasks.join(",");
  }
  return fetchJson<UsersListResponse>(
    `/v1/dashboard/users${buildQuery(window, extra)}`,
    {
      token,
    },
  );
}

export function fetchQuotas(
  userIds: string[],
  token: string | null,
): Promise<BulkQuotaResponse> {
  return fetchJson<BulkQuotaResponse>("/v1/dashboard/quotas", {
    token,
    method: "POST",
    body: JSON.stringify({ userIds }),
  });
}

export function fetchUserQuota(
  userId: string,
  token: string | null,
): Promise<QuotaInfo> {
  return fetchJson<QuotaInfo>(
    `/v1/dashboard/users/${encodeURIComponent(userId)}/quota`,
    { token },
  );
}

export function resetUserQuota(
  userId: string,
  token: string | null,
): Promise<{ userId: string; purged: boolean }> {
  return fetchJson<{ userId: string; purged: boolean }>(
    `/v1/dashboard/users/${encodeURIComponent(userId)}/quota`,
    { token, method: "DELETE" },
  );
}

export function fetchUsage(
  window: UsageWindow,
  token: string | null,
): Promise<UsageResponse> {
  const params = new URLSearchParams({ window });
  return fetchJson<UsageResponse>(`/v1/dashboard/usage?${params}`, { token });
}

export function fetchUserDetail(
  userId: string,
  window: DashboardWindow,
  token: string | null,
): Promise<UserDetailResponse> {
  return fetchJson<UserDetailResponse>(
    `/v1/dashboard/users/${encodeURIComponent(userId)}${buildQuery(window)}`,
    { token },
  );
}
